import { useCallback, useState } from 'react'

/**
 * Doing something to a list, and saying what happened.
 *
 * Four screens had this written out — the team, sending domains, suppressions,
 * API keys — and the shape was identical every time: clear what was on screen,
 * disable the controls, do the thing, reload, then either say what happened or
 * say what did not. jscpd found the fourth copy the moment it was written,
 * which is exactly what it is for.
 *
 * Three decisions live here rather than in each screen.
 *
 * **It always reloads.** The server decides what is on a list — a suppression
 * can arrive from a bounce webhook between two clicks, a colleague can revoke
 * an invitation — and a screen that patched its own rows would show somebody a
 * list the product does not agree with.
 *
 * **A failure says so and changes nothing on screen.** The reload does not run,
 * so the last known-good list stays rather than being replaced by an empty one.
 *
 * **A notice and a problem are never both showing.** They are the same slot in
 * a person's attention, and two contradictory banners is worse than either.
 */

export interface Action {
  /** What happened, when the work had something worth saying. */
  readonly notice: string | undefined
  /** What did not happen. Never set at the same time as `notice`. */
  readonly problem: string | undefined
  /** True while the work is in flight, for disabling the controls that start it. */
  readonly busy: boolean
  /**
   * Runs `work`, reloads, and reports.
   *
   * `work` returns the sentence to show, or undefined when the outcome speaks
   * for itself — a row that has plainly gone needs no announcement. Resolves
   * true when the work completed, so a caller can clear a form only on success.
   */
  readonly run: (work: () => Promise<string | undefined>, failure: string) => Promise<boolean>
}

export function useAction(reload: () => Promise<void>): Action {
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const run = useCallback(
    async (work: () => Promise<string | undefined>, failure: string) => {
      setNotice(undefined)
      setProblem(undefined)
      setBusy(true)
      try {
        const said = await work()
        await reload()
        if (said !== undefined) setNotice(said)
        return true
      } catch {
        setProblem(failure)
        return false
      } finally {
        setBusy(false)
      }
    },
    [reload],
  )

  return { notice, problem, busy, run }
}
